import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Tooltip } from 'antd';
import { RobotOutlined, WarningOutlined } from '@ant-design/icons';

import { PageHeader } from '../../../components/shared';
import { Badge, Empty } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import { HopXacNhan } from '../components/chung/HopXacNhan';
import { LoiVan } from '../components/chung/LoiVan';
import { DaiLoi } from '../components/chung/VungDuLieu';
import { ChonGiong } from '../components/hoi/ChonGiong';
import { DanhSachDoan, type MucDoan } from '../components/hoi/DanhSachDoan';
import { KhoiSoLieuDaLuu } from '../components/hoi/KhoiSoLieuDaLuu';
import { BongHoi, LuotChatBot } from '../components/hoi/LuotHoi';
import { ThanhSoan } from '../components/hoi/ThanhSoan';
import { ghiNho, nho } from '../hooks/ghi-nho';
import { useCoNghe, useDocTo, useNghe } from '../hooks/giong';
import { useBoTriThuc, useBotDungDuoc, useDanhSachGiong, useSucKhoeTroLy } from '../hooks/useChung';
import { useMoTaLoi } from '../hooks/useMoTaLoi';
import {
  useDoanChatBot,
  useGhiDoanBot,
  useLuotHoi,
  useMoDoanBot,
  useXoaDoanBot,
  type TuyChonLuotHoi,
} from '../hooks/useHoi';
import { BAC_WEB, chuanHoa, goiSoLieu, lichSuTuTinNhan } from '../nghiep-vu/hoi';
import { tenCongKhaiMacDinh } from '../nghiep-vu/ten-model';
import type { ChatMessage } from '../types';

/** Một tin nhắn đã lưu — mở lại đoạn cũ phải thấy ĐÚNG thứ đã thấy lúc hỏi, kể cả biểu đồ và bảng. */
function DongCu({ m }: { m: ChatMessage }) {
  const { t, locale } = useT();
  if (m.author === 'user') return <BongHoi>{m.text}</BongHoi>;
  const rowCount = m.payload?.rowCount;
  return (
    <div className="space-y-1">
      <LoiVan text={m.text} className="text-sm leading-relaxed text-content-primary" />
      <KhoiSoLieuDaLuu payload={m.payload} />
      {typeof rowCount === 'number' ? (
        <div className="flex items-center gap-2 pt-1 text-xs text-content-muted">
          <span>{t('troLyAiHoiLuot.dong', { n: soVN(rowCount, locale) })}</span>
          <span>· {t('troLyAiHoi.daLuuLuot')}</span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Chat với một bot đã cấu hình (`/tro-ly-ai/tro-chuyen/:botId`, port `BotChatView.tsx` native). Dùng chung luồng SSE với
 * Hỏi đáp AI; bản khai của bot quyết định tra ở đâu, kênh giao tiếp và giọng.
 */
export function BotChatView() {
  const { botId = '' } = useParams();
  const { t, locale } = useT();
  const dieuHuong = useDieuHuong();
  const moTaLoi = useMoTaLoi();
  const [cvId, setCvId] = useState<string | null>(null);
  const [model, setModel] = useState('');
  // Ba công tắc NHỚ RIÊNG cho màn này: hai màn hỏi hai thứ khác nhau, dùng chung khoá là đổi ở đây làm đổi màn kia.
  const [mucVan, setMucVan] = useState(() => nho('bot.mucVan', 'gon'));
  const [doDai, setDoDai] = useState(() => nho('bot.doDai', 'vua'));
  const [bieuDo, setBieuDo] = useState(() => nho('bot.bieuDo', 'tu_dong'));
  // Mặc định KHÔNG TRA — `tu_dong` cộng 6–7 giây vào mọi lượt kho tri thức không khớp.
  const [web, setWeb] = useState(() => nho('bot.web', 'khong'));
  const [question, setQuestion] = useState('');
  const [canXoa, setCanXoa] = useState<MucDoan | null>(null);
  const cuoi = useRef<HTMLDivElement>(null);
  const nghe = useNghe(setQuestion);

  const health = useSucKhoeTroLy();
  const llmOff = health.data && !health.data.llmConfigured;

  const bots = useBotDungDuoc();
  const bot = bots.data?.find((b) => b.id === botId);
  // Nhóm "tra web" chỉ có nghĩa khi bot khai ít nhất một tên miền.
  const coWeb = !!bot?.webDomains?.length;

  // ── KÊNH GIAO TIẾP — ĐỌC TỪ BẢN KHAI CỦA BOT ── `van_ban` không dựng gì: không mic, không gửi `giong`.
  const kenh = bot?.cheDoGiaoTiep ?? 'van_ban';
  const coTieng = kenh !== 'van_ban';
  // `giong_noi` đọc ngay; `ca_hai` để người dùng bật loa.
  const [batDoc, setBatDoc] = useState(() => nho('bot.doc') === '1');
  const doc = useDocTo();
  const dsGiong = useDanhSachGiong();
  const [giong, setGiong] = useState(() => nho('bot.giong', ''));
  const giongDung = (giong && dsGiong.some((g) => g.ma === giong) ? giong : dsGiong[0]?.ma) ?? null;
  const coNghe = useCoNghe();

  const phatRef = useRef(false);
  useEffect(() => {
    phatRef.current = kenh === 'giong_noi' || (kenh === 'ca_hai' && batDoc);
  }, [kenh, batDoc]);

  // Tên bộ tri thức lấy từ module tri thức — bot chỉ giữ mã.
  const boTriThuc = useBoTriThuc(!!bot?.knowledgeBaseIds.length);
  const boTen = useMemo(
    () =>
      (bot?.knowledgeBaseIds ?? [])
        .map((k) => boTriThuc.data?.find((c) => c.id === k)?.ten)
        .filter((x): x is string => !!x),
    [bot, boTriThuc.data],
  );

  const danhSach = useDoanChatBot(botId, !!bot?.saveHistory);
  const dangMo = useMoDoanBot(botId, cvId, !!bot);
  const xoa = useXoaDoanBot(botId);
  const ghi = useGhiDoanBot(botId);

  // ID tin nhắn CHÍNH PHIÊN NÀY vừa ghi — `turns` đang vẽ chúng rồi.
  const [daGhi, setDaGhi] = useState<Set<string>>(new Set());
  const oLichSu = useRef({ ms: [] as ChatMessage[], daGhi });
  useEffect(() => {
    oLichSu.current = { ms: dangMo.data?.messages ?? [], daGhi };
  }, [dangMo.data, daGhi]);

  const tuyChonLuot = useMemo<TuyChonLuotHoi>(
    () => ({
      // Kiểm Ở ĐÂY chứ không chỉ lúc gửi: tắt loa GIỮA lượt thì những mẩu còn lại phải im.
      onTieng: (stt, goi) => {
        if (phatRef.current) doc.them(stt, goi);
      },
      onXongTieng: () => doc.xong(),
      // Ngữ cảnh của đoạn ĐANG MỞ — đọc lúc gửi, vì `dangMo` về sau lần dựng đầu.
      lichSuCu: () => lichSuTuTinNhan(oLichSu.current.ms, oLichSu.current.daGhi),
    }),
    [doc],
  );
  const { turns, dangChay, hoi, dung, xoaHet } = useLuotHoi(tuyChonLuot);

  const chuyenDoan = useCallback(
    (moi: string | null) => {
      if (moi !== cvId) {
        xoaHet();
        setDaGhi(new Set());
      }
      setCvId(moi);
    },
    [cvId, xoaHet],
  );

  useEffect(() => {
    cuoi.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns, dangMo.data]);

  const gui = async (q = question) => {
    if (!q.trim() || !bot || dangChay) return;
    setQuestion('');
    // LƯỢT MỚI: im ngay và đặt lại con trỏ đọc về 0 — không thì bốn mẩu đầu của lượt sau bị bỏ vì "đã nhận".
    doc.dung();

    let doan = cvId;
    if (!doan && bot.saveHistory) {
      try {
        doan = await ghi.taoDoan();
        setCvId(doan);
      } catch {
        // Lưu hỏng KHÔNG chặn việc hỏi.
        doan = null;
      }
    }

    const kq = await hoi({
      question: q,
      chatbotId: botId,
      conversationId: doan ?? undefined,
      maxRows: 200,
      model: model || undefined,
      mucVan,
      doDai,
      bieuDo,
      // `auto` khi chưa có danh sách giọng — gửi `undefined` là máy chủ không tổng hợp gì, im lặng.
      giong: coTieng ? (giongDung ?? 'auto') : undefined,
      tiengDan: true,
      // Chỉ gửi khi bot có khai tên miền; giá trị cũ (`co`) rơi về `khong`.
      web: coWeb ? chuanHoa(BAC_WEB, web, 'khong') : undefined,
    });

    if (!doan || !kq?.answer) return;
    try {
      const [mUser, mBot] = await ghi.ghiLuot(doan, q, kq.answer, {
        sql: kq.sql,
        rowCount: kq.rowCount,
        kind: kq.kind,
        ...(kq.traceId ? { traceId: kq.traceId } : {}),
        ...(goiSoLieu(kq) ?? {}),
      });
      setDaGhi((p) => new Set(p).add(mUser).add(mBot));
    } catch {
      /* lưu hỏng thì thôi */
    }
  };

  const breadcrumb = [
    { label: t('nav.nhom.troLiAi') },
    { label: t('troLyAiHoiBot.tatCaBot'), onClick: () => dieuHuong('/tro-ly-ai/tro-chuyen') },
    { label: bot?.name ?? t('troLyAiHoiBot.tieuDe') },
  ];

  if (bots.isError) {
    return (
      <div className="flex flex-col gap-3 p-3">
        <PageHeader breadcrumb={breadcrumb} title={t('troLyAiHoiBot.tieuDe')} />
        <div className="rounded-lg border border-default bg-surface">
          <DaiLoi loi={bots.error} onThuLai={() => void bots.refetch()} />
        </div>
      </div>
    );
  }
  if (!bots.isPending && !bot) {
    return (
      <div className="flex flex-col gap-3 p-3">
        <PageHeader breadcrumb={breadcrumb} title={t('troLyAiHoiBot.tieuDe')} />
        <div className="rounded-lg border border-default bg-surface">
          <Empty icon={RobotOutlined} title={t('troLyAiHoiBot.khongCoBot', { id: botId })} />
        </div>
      </div>
    );
  }

  const troiTrong = !dangMo.data?.messages.length && !turns.length;
  // Lời chào dùng chung với khung nhúng (`widget.greeting`). KHÔNG lưu thành tin nhắn: chữ của bản khai, không phải một
  // lượt hội thoại — sửa cấu hình xong mọi đoạn trống hiện ngay lời chào mới.
  const loiChao = bot?.active === false ? '' : (bot?.widget?.greeting ?? '').trim();
  const dsDoan = danhSach.data?.map((c) => ({ id: c.id, ten: c.title || t('troLyAiHoiBot.doanChatMoi') }));

  return (
    <div className="flex h-full flex-col gap-3 p-3">
      <PageHeader
        breadcrumb={breadcrumb}
        title={bot?.name ?? t('troLyAiHoiBot.tieuDe')}
        subtitle={bot?.shortDescription || t('troLyAiHoiBot.phuDe')}
      />

      <div className="flex min-h-0 flex-1 gap-3">
        <DanhSachDoan
          nhanMoi={t('troLyAiHoiBot.doanChatMoi')}
          moiDuoc={!!bot?.saveHistory}
          onMoi={() => chuyenDoan(null)}
          ds={bot?.saveHistory ? dsDoan : []}
          dangChon={cvId}
          dangTai={!!bot?.saveHistory && danhSach.isPending}
          rong={
            !bot?.saveHistory
              ? t('troLyAiHoiBot.tatLuuLichSu')
              : danhSach.isError
                ? moTaLoi(danhSach.error)
                : t('troLyAiHoiBot.chuaCoDoanChat')
          }
          onChon={(id) => chuyenDoan(id)}
          onXoa={setCanXoa}
          nhanXoa={(ten) => t('troLyAiHoiBot.xoaDoanChat', { ten })}
          chan={t('troLyAiHoiBot.doanTheoQuyen')}
        />

        <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
          <header className="flex min-h-14 shrink-0 flex-wrap items-center justify-between gap-3 border-b border-default px-4 py-2">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
                <RobotOutlined />
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-content-primary">{bot?.name ?? '…'}</p>
                {bot?.shortDescription ? (
                  <p className="truncate text-xs text-content-secondary">{bot.shortDescription}</p>
                ) : null}
              </div>
            </div>
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              {/* Giọng là trạng thái của CẢ ĐOẠN CHAT — header, cùng chỗ và cùng thành phần với Hỏi đáp AI. Kênh `van_ban`
                  nói RA LÝ DO thay vì để trống. */}
              {coTieng ? (
                <ChonGiong
                  danhSach={dsGiong}
                  giong={giongDung}
                  onDoiGiong={(v) => {
                    setGiong(v);
                    ghiNho('bot.giong', v);
                  }}
                  // `giong_noi` đọc mọi câu theo bản khai — công tắc luôn bật và không đổi được.
                  bat={kenh === 'giong_noi' ? true : batDoc}
                  onBat={(v) => {
                    if (kenh === 'giong_noi') return;
                    setBatDoc(v);
                    ghiNho('bot.doc', v ? '1' : '0');
                    if (!v) doc.dung();
                  }}
                  dangDoc={doc.dangDoc}
                  dangTam={doc.dangTam}
                  onTam={doc.bamTam}
                />
              ) : (
                <Tooltip title={t('troLyAiHoiBot.botVanBanGhi')}>
                  <span>
                    <Badge variant="neutral">{t('troLyAiHoiBot.botVanBan')}</Badge>
                  </span>
                </Tooltip>
              )}
              {boTen.map((n) => (
                <Badge key={n} variant="neutral">
                  {n}
                </Badge>
              ))}
              {bot?.active === false && <Badge variant="warning">{t('troLyAiHoiBot.botDangTat')}</Badge>}
              {bot && (
                <Badge variant={bot.mode === 'open' ? 'warning' : 'success'}>
                  {bot.mode === 'open' ? t('troLyAiHoiBot.modeOpen') : t('troLyAiHoiBot.modeKnowledgeOnly')}
                </Badge>
              )}
              {/* Từng là CẢNH BÁO (bot nhiều nguồn chỉ hỏi nguồn đầu). Giờ mỗi nguồn một lượt tra riêng — chỉ còn là lời
                  kể; giữ màu vàng cho một hành vi đúng là dạy người dùng bỏ qua màu vàng. */}
              {bot && bot.sourceIds.length > 1 && (
                <Tooltip title={t('troLyAiHoiBot.nhieuNguonGhi')}>
                  <span>
                    <Badge variant="neutral">{t('troLyAiHoiBot.nhieuNguon', { n: soVN(bot.sourceIds.length, locale) })}</Badge>
                  </span>
                </Tooltip>
              )}
            </div>
          </header>

          <div className="flex-1 overflow-y-auto px-4 py-5 lg:px-6" aria-live="polite">
            <div className="mx-auto flex min-h-full w-full max-w-5xl flex-col space-y-6">
              {dangMo.isError ? <p className="text-xs text-danger-700">{moTaLoi(dangMo.error)}</p> : null}
              {dangMo.data?.messages
                .filter((m) => !daGhi.has(m.id))
                .map((m) => (
                  <DongCu key={m.id} m={m} />
                ))}

              {turns.map((turn) => (
                <LuotChatBot key={turn.id} turn={turn} onHoiTiep={(q) => void gui(q)} chiGiongNoi={kenh === 'giong_noi'} />
              ))}

              {troiTrong && loiChao ? (
                <LoiVan text={loiChao} className="text-sm leading-relaxed text-content-primary" />
              ) : null}

              {troiTrong && !loiChao ? (
                <div className="flex flex-1 flex-col items-center justify-center text-center">
                  <div className="my-auto max-w-md space-y-3 p-6">
                    <span className="mx-auto flex size-12 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
                      <RobotOutlined className="text-xl" />
                    </span>
                    <h3 className="text-base font-semibold text-content-primary">{bot?.name ?? t('troLyAiHoiBot.tieuDe')}</h3>
                    {/* KHÔNG hiện `systemPrompt`: đó là lời nhắc cho MÔ HÌNH, bày ra là lộ cấu hình nội bộ. */}
                    <p className="text-xs leading-relaxed text-content-secondary">
                      {bot?.active === false ? t('troLyAiHoiBot.moBotLai') : bot?.shortDescription || t('troLyAiHoiBot.hoiMotCau')}
                    </p>
                  </div>
                </div>
              ) : null}
              <div ref={cuoi} />
            </div>
          </div>

          <ThanhSoan
            question={question}
            onQuestion={setQuestion}
            onGui={() => void gui()}
            onDung={dung}
            dangChay={dangChay}
            placeholder={t('troLyAiHoiBot.hoiBot', { ten: bot?.name ?? t('troLyAiHoiBot.botMacDinh') })}
            guiDuoc={!!bot}
            tuyChon={{
              mucVan,
              doDai,
              bieuDo,
              onMucVan: (v) => {
                setMucVan(v);
                ghiNho('bot.mucVan', v);
              },
              onDoDai: (v) => {
                setDoDai(v);
                ghiNho('bot.doDai', v);
              },
              onBieuDo: (v) => {
                setBieuDo(v);
                ghiNho('bot.bieuDo', v);
              },
            }}
            web={
              coWeb
                ? {
                    gia: web,
                    onWeb: (v) => {
                      setWeb(v);
                      ghiNho('bot.web', v);
                    },
                  }
                : undefined
            }
            model={model}
            onModel={setModel}
            // Mic dựng CÓ ĐIỀU KIỆN — cùng luật với khung nhúng: bot có kênh tiếng và trình duyệt có API nhận giọng.
            mic={coTieng && coNghe ? nghe : undefined}
            canhBao={
              <>
                {bot?.active === false ? (
                  <p className="mb-2 flex items-center gap-1.5 text-xs text-warning-700">
                    <WarningOutlined className="shrink-0 text-xs" /> {t('troLyAiHoiBot.botDangTatGhi')}
                  </p>
                ) : null}
                {llmOff ? (
                  <p className="mb-2 flex items-start gap-1.5 text-xs text-warning-700">
                    <WarningOutlined className="mt-0.5 shrink-0 text-xs" />{' '}
                    {t('troLyAiHoiBot.thieuApiKey', { model: tenCongKhaiMacDinh(health.data?.model, health.data?.models) })}
                  </p>
                ) : null}
              </>
            }
            goiY={t('troLyAiHoi.enterDeGuiNgan')}
          />
        </div>
      </div>

      <HopXacNhan
        open={!!canXoa}
        onClose={() => {
          setCanXoa(null);
          xoa.reset();
        }}
        tieuDe={t('troLyAiHoi.xacNhanXoaTieuDe', { ten: canXoa?.ten ?? '' })}
        than={t('troLyAiHoi.xacNhanXoaNoiDung')}
        nhanXacNhan={t('troLyAiHoi.xoaDoanNut')}
        dangChay={xoa.isPending}
        loi={xoa.error}
        onXacNhan={() => {
          if (!canXoa) return;
          xoa.mutate(canXoa.id, {
            onSuccess: () => {
              if (cvId === canXoa.id) chuyenDoan(null);
              setCanXoa(null);
            },
          });
        }}
      />
    </div>
  );
}
